import i18n from 'i18next'
import type { TFunction } from 'i18next'
import { initReactI18next } from 'react-i18next'
import MAPPING from './locales/_mapping.json'
// Mở rộng kiểu cho `t()` — SINH TỰ ĐỘNG. Import phụ-tác-dụng vì `declare module` chỉ có hiệu lực
// khi tệp nằm trong chương trình biên dịch. Tệp chỉ có `import type` nên nó rỗng lúc chạy.
import './types.ts'

/**
 * ══ KHỞI TẠO i18next — THIẾT KẾ B: **MỘT** NAMESPACE `translation`, NẠP TRỄ THEO NHÓM KHOÁ ══════
 *
 * ⛔ ĐỌC KỸ TRƯỚC KHI SỬA — đây là chỗ dễ đấu sai nhất của cả dự án, và đấu sai thì KHÔNG hỏng một
 *    màn mà hỏng **TOÀN BỘ** giao diện.
 *
 * Trên đĩa, kho chuỗi nằm ở `locales/<lng>/<nhóm>.json` + `_mapping.json` — **trông y hệt** quy ước
 * i18next "mỗi tệp = một namespace" (gọi là THIẾT KẾ A). Nhưng mọi lời gọi chép từ `proship-trans`
 * đều là `t('nav.az')` — khoá **CÓ DẤU CHẤM trong MỘT namespace mặc định**, không phải `t('nav:az')`:
 *
 *     THIẾT KẾ A (mỗi tệp = 1 namespace):  t('nav.az') → "nav.az"        ← khoá thô, MỌI màn
 *     THIẾT KẾ B (gộp `translation`):      t('nav.az') → "LOGISTICS RT"  ← ĐÚNG
 *
 * `months.json` là MẢNG ở cấp cao nhất càng mời người đọc về thiết kế A. Task 20 đã khoá hợp đồng
 * này bằng máy: `scripts/migrate-i18n.test.ts` ▸ *"HỢP ĐỒNG ĐẤU DÂY"* dựng `i18next.createInstance()`
 * THẬT trên hiện vật đã commit. Ca kiểm `i18n.test.ts` ▸ *"THIẾT KẾ B"* dưới đây canh chính tệp này.
 *
 * ⇒ **"Nhóm khoá" ≠ "namespace i18next".** 37 tệp JSON là 37 **nhóm khoá** — mỗi nhóm là một khoá
 *   CẤP MỘT bên trong namespace duy nhất `translation`. Vì thế mọi thứ ở đây gọi là `KEY_GROUPS`,
 *   KHÔNG gọi là namespace: đặt tên sai là mời người sau đấu lại theo thiết kế A.
 *
 * ══ NẠP TRỄ THẾ NÀO KHI CHỈ CÓ MỘT NAMESPACE ═══════════════════════════════════════════════════
 * Cơ chế nạp trễ sẵn có của i18next (`ns: [...]` + backend) chia theo **namespace**, tức thiết kế A
 * — dùng không được. Thay bằng: `import()` động đúng tệp của nhóm cần dùng rồi **gộp vào namespace
 * `translation`** bằng `addResourceBundle(lng, 'translation', { [nhóm]: dữ liệu }, true, true)`.
 * Vite/Rollup tách mỗi tệp JSON thành một chunk riêng (`dist/assets/locales/*.js` — xem
 * `apps/web/vite.config.ts`), nên gói khởi động KHÔNG mang theo 1,7 MB kho chuỗi.
 *
 * Bản cũ tải **cả 3 ngôn ngữ × cả 45 nhóm** ngay từ đầu (`i18n.ts` 1,6 MB nằm trong gói). Ở đây,
 * lúc khởi động chỉ nạp `STARTUP_KEY_GROUPS` của MỘT ngôn ngữ (+ ngôn ngữ lùi nếu khác).
 */

/** Ba ngôn ngữ bắt buộc (CLAUDE.md §9). */
export type Lng = 'vi' | 'en' | 'zh'
export const LNGS: readonly Lng[] = ['vi', 'en', 'zh']

/** Ngôn ngữ LÙI VỀ. Mọi khoá thiếu ở en/zh rơi về đây — nên nó LUÔN được nạp cùng. */
export const FALLBACK_LNG: Lng = 'vi'

/** Tên namespace i18next DUY NHẤT. Đây là toàn bộ thiết kế B trong một hằng số. */
export const MERGED_NS = 'translation'

/**
 * 37 **nhóm khoá** RT — đọc từ `_mapping.json` do `pnpm i18n:migrate` sinh (Task 20), KHÔNG gõ tay.
 * ⚠ Đây KHÔNG phải namespace i18next (xem khối chú thích đầu tệp).
 */
export const KEY_GROUPS: readonly string[] = Object.freeze(Object.keys(MAPPING))

/**
 * Nhóm khoá nạp NGAY lúc khởi động — đúng những gì màn hình ĐẦU TIÊN cần, không hơn:
 *  · `nav`    — sidebar (`AppLayout`)
 *  · `common` — nút Đăng xuất, nhãn mặc định của `AlertModal`/`ConfirmModal`, câu "không có quyền"
 *  · `login`  — màn đăng nhập, kể cả bảng câu lỗi `login.loi.*`
 *  · `roles`  — ⭐ dòng TÊN VAI cạnh nút Đăng xuất (`Layout.tsx:798` bản cũ, `t(\`roles.${role}\`)`).
 *               Bỏ nhóm này ra là sidebar của **mọi người đăng nhập** hiện `roles.ke_toan` ở
 *               production và NÉM LỖI ở dev. Ca kiểm "tên vai hiện ra cho cả 8 vai" canh đúng đây.
 *  · `app`    — ⭐ G1/G3: TÊN CÔNG TY + `alt` của logo ở đầu header (`Layout.tsx:721-722` bản cũ).
 *               Cùng họ với `roles`: `AppLayout` render nó ở MỌI màn nên phải có mặt từ lúc khởi
 *               động, bỏ ra là header hiện chữ thô `app.name`. Nhóm này chỉ có 2 khoá.
 */
export const STARTUP_KEY_GROUPS: readonly string[] = Object.freeze(['common', 'nav', 'login', 'roles', 'app'])

type JsonFile = Record<string, unknown>
const groupToFile = MAPPING as Record<string, string>

/**
 * Dev/test thì khoá thiếu là LỖI; production thì chỉ ghi log.
 * `import.meta.env` chỉ có ở Vite/Vitest, `process.env` chỉ có ở Node — gói này chạy cả hai nơi.
 */
const isDevOrTest = (): boolean => {
  const env = (import.meta as ImportMeta & { env?: { DEV?: boolean } }).env
  if (typeof env?.DEV === 'boolean') return env.DEV
  const proc = (globalThis as { process?: { env?: Record<string, string | undefined> } }).process
  return (proc?.env?.NODE_ENV ?? 'development') !== 'production'
}

/** Nhóm khoá ĐÃ nạp, theo từng ngôn ngữ — để đổi ngôn ngữ thì nạp lại đúng chừng đó, không hơn. */
const loadedGroups = new Map<Lng, Set<string>>()
const loadedGroupsFor = (lng: Lng): Set<string> => {
  const s = loadedGroups.get(lng) ?? new Set<string>()
  loadedGroups.set(lng, s)
  return s
}

/**
 * Nạp MỘT tệp JSON của một nhóm khoá.
 * `import()` với biến: Vite/Rollup biến thành một glob trên `./locales` ⇒ mỗi tệp một chunk riêng.
 */
const readGroupFile = async (lng: Lng, groups: string): Promise<JsonFile[string]> => {
  const file = groupToFile[groups]?.replace(/\.json$/, '')
  if (file === undefined) {
    throw new Error(
      `Nhóm khoá i18n không tồn tại: "${groups}". Nhóm hợp lệ do \`pnpm i18n:migrate\` sinh vào ` +
        `locales/_mapping.json (${KEY_GROUPS.length} nhóm).`,
    )
  }
  // ⚠ Đuôi `.json` phải nằm ở phần TĨNH của chuỗi — `vite:dynamic-import-vars` không dựng nổi
  //   glob nếu đuôi tệp cũng là biến (cảnh báo "A file extension must be included").
  const mod = (await import(`./locales/${lng}/${file}.json`)) as { default: JsonFile[string] }
  return mod.default
}

/** Gom nhiều nhóm khoá của MỘT ngôn ngữ thành khối `{ nhóm: dữ liệu }` — hình dạng của `translation`. */
const bundleGroups = async (lng: Lng, groups: readonly string[]): Promise<JsonFile> => {
  const parts = await Promise.all(groups.map(async (n) => [n, await readGroupFile(lng, n)] as const))
  return Object.fromEntries(parts)
}

/**
 * Nạp thêm nhóm khoá vào kho ĐANG CHẠY — **gộp vào `translation`**, không tạo namespace mới.
 *
 * Nạp cho ngôn ngữ đang dùng VÀ ngôn ngữ lùi: khoá thiếu ở en/zh rơi về `vi` (`fallbackLng`), mà
 * `vi` chưa nạp thì nó rơi tiếp vào `missingKeyHandler` và NÉM — thiếu chỗ này là một lớp lỗi chỉ
 * lộ ra khi người dùng đổi sang tiếng Anh.
 *
 * 👉 Màn hình ngoài `STARTUP_KEY_GROUPS` PHẢI gọi hàm này (và `await`) TRƯỚC khi vẽ; chưa nạp mà
 *    gọi `t()` là ném ở dev, đúng thiết kế P16.
 */
export const loadKeyGroups = async (...groups: readonly string[]): Promise<void> => {
  const current = (i18n.resolvedLanguage ?? i18n.language ?? FALLBACK_LNG) as Lng
  const neededLngs = current === FALLBACK_LNG ? [current] : [current, FALLBACK_LNG]
  for (const lng of neededLngs) {
    const have = loadedGroupsFor(lng)
    const missing = groups.filter((n) => !have.has(n))
    if (missing.length === 0) continue
    const bundle = await bundleGroups(lng, missing)
    // deep = true, overwrite = true — gộp CẠNH các nhóm đã có, không thay cả bó.
    i18n.addResourceBundle(lng, MERGED_NS, bundle, true, true)
    for (const n of missing) have.add(n)
  }
}

/** Đổi ngôn ngữ AN TOÀN: nạp lại đúng những nhóm đang dùng cho ngôn ngữ mới RỒI mới đổi. */
export const switchLanguage = async (lng: Lng): Promise<void> => {
  const target = (LNGS as readonly string[]).includes(lng) ? lng : FALLBACK_LNG
  const needed = [...loadedGroupsFor((i18n.resolvedLanguage ?? FALLBACK_LNG) as Lng)]
  const have = loadedGroupsFor(target)
  const missing = needed.filter((n) => !have.has(n))
  if (missing.length > 0) {
    i18n.addResourceBundle(target, MERGED_NS, await bundleGroups(target, missing), true, true)
    for (const n of missing) have.add(n)
  }
  await i18n.changeLanguage(target)
}

/**
 * ⭐ P16 — KHOÁ THIẾU LÀ **LỖI**, không phải chữ trống trên màn hình.
 *
 * i18next mặc định trả về CHÍNH CHUỖI KHOÁ khi thiếu, nên `nav.azPurchaseDocs` hiện thẳng lên
 * sidebar mà không ai biết. Ở dev + test thì NÉM (`send()` của i18next gọi handler này KHÔNG bọc
 * try/catch, nên lỗi thoát ra tới người gọi `t()` — có ca kiểm khẳng định). Ở production thì chỉ
 * `console.error`: một khoá thiếu không được phép làm trắng màn hình của kế toán đang chốt sổ.
 *
 * ⚠ Đây mới là NỬA RUNTIME của lưới. Nó chỉ bắt khoá được GỌI THẬT — khoá của màn chưa ai mở thì
 *   không bao giờ bắn. Nửa TĨNH là cổng CI `i18n:missing` (quét mã → đối chiếu kho), **việc của
 *   Task 22**; thiếu nó thì lưới hở đúng phần lớn nhất.
 */
const onMissingKey = (lngs: readonly string[], _ns: string, key: string): void => {
  const msg =
    `Thiếu khoá i18n: ${key} (${String(lngs)}). ` +
    `Hoặc kho chuỗi chưa có khoá này (thêm ĐỦ 3 ngôn ngữ — CLAUDE.md §9), ` +
    `hoặc nhóm khoá "${key.split('.')[0] ?? key}" chưa được loadKeyGroups() trước khi vẽ màn.`
  if (isDevOrTest()) throw new Error(msg)
  console.error('[i18n]', msg)
}

/**
 * Khởi tạo. Gọi MỘT LẦN trước khi vẽ (`apps/web/src/main.tsx`); gọi lại chỉ đổi ngôn ngữ.
 * Ở test, gọi trong `beforeAll` là đủ — lời gọi thứ hai không dựng lại thể hiện.
 */
export const initI18n = async (lng: Lng = FALLBACK_LNG): Promise<void> => {
  const target = (LNGS as readonly string[]).includes(lng) ? lng : FALLBACK_LNG
  if (i18n.isInitialized) {
    await switchLanguage(target)
    return
  }

  const neededLngs: Lng[] = target === FALLBACK_LNG ? [target] : [target, FALLBACK_LNG]
  const resources: Record<string, Record<string, JsonFile>> = {}
  for (const l of neededLngs) {
    resources[l] = { [MERGED_NS]: await bundleGroups(l, STARTUP_KEY_GROUPS) }
    const have = loadedGroupsFor(l)
    for (const n of STARTUP_KEY_GROUPS) have.add(n)
  }

  await i18n.use(initReactI18next).init({
    lng: target,
    fallbackLng: FALLBACK_LNG,
    supportedLngs: LNGS as string[],
    // ⭐ THIẾT KẾ B — MỘT namespace duy nhất. Đổi hai dòng này là hỏng toàn bộ giao diện.
    ns: [MERGED_NS],
    defaultNS: MERGED_NS,
    // Chỉ có MỘT namespace nên dấu ':' không còn ý nghĩa phân tách; tắt để không khoá nào bị
    // hiểu nhầm thành `ns:key`.
    nsSeparator: false,
    resources,
    interpolation: { escapeValue: false },
    // `saveMissing` BẬT CẢ Ở PRODUCTION — nếu tắt thì `khoaThieu` không bao giờ chạy và nhánh
    // `console.error` (chủ đích của P16 cho production) thành mã chết.
    saveMissing: true,
    missingKeyHandler: onMissingKey,
    // `addResourceBundle` phát sự kiện `added`; không buộc vào đây thì màn đã vẽ KHÔNG vẽ lại sau
    // khi nhóm khoá của nó vừa nạp xong.
    react: { bindI18nStore: 'added' },
  })

  // Đổi ngôn ngữ bằng `i18n.changeLanguage()` THẲNG (VD bộ chọn ngôn ngữ của Task 23) vẫn phải
  // chạy đúng: nạp bù các nhóm đang dùng cho ngôn ngữ mới. `switchLanguage()` là đường CÓ `await`.
  i18n.on('languageChanged', (l: string) => {
    if (typeof document !== 'undefined') document.documentElement.lang = l
    const next = l as Lng
    if (!(LNGS as readonly string[]).includes(next)) return
    const needed = [...loadedGroupsFor(FALLBACK_LNG)].filter((n) => !loadedGroupsFor(next).has(n))
    if (needed.length === 0) return
    void loadKeyGroups(...needed)
  })
  if (typeof document !== 'undefined') document.documentElement.lang = target
}

/**
 * Khoá DỰNG ĐỘNG lúc chạy — `t(\`nav.${item.key}\`)` · `t(\`roles.${me.role}\`)`, đúng như bản cũ
 * làm (`Layout.tsx:274,798`). Sau khi sinh kiểu thì `t()` chỉ nhận khoá LITERAL, nên hai chỗ đó
 * cần MỘT cửa riêng ghi rõ ràng thay vì rắc ép kiểu khắp nơi.
 *
 * ⚠ Cổng tĩnh `i18n:missing` của **Task 22** PHẢI xử được các lời gọi đi qua cửa này (quét tiền tố
 *   `nav.` / `roles.` rồi đối chiếu cả nhóm) — nếu không nó sẽ báo động giả rồi bị tắt đi.
 */
/*
 * ⭐ `values` — GIÁ TRỊ ĐIỀN VÀO CÂU, tuỳ chọn. Thêm cho bộ rà sổ tiền (`0079`): mã câu đến từ máy chủ
 *   nên khoá là động, mà câu lại có chỗ trống (`{{ref}}` · `{{paid}}`). Tham số thứ ba KHÔNG đổi gì
 *   với cổng `i18n:missing` — cổng chỉ đọc đối số thứ hai (khoá) để phân giải tiền tố.
 */
export const tDynamic = (
  t: TFunction, key: string, values?: Readonly<Record<string, string | number>>,
): string => (t as (k: string, v?: Readonly<Record<string, string | number>>) => string)(key, values)

export default i18n
